import {Component, OnDestroy, OnInit} from '@angular/core';
import {CompetitionService} from '../../../../services/competition/competition.service';
import {CountryModel} from '../../../../models/localization-and-pricing/country.model';
import {ActivatedRoute, Params} from '@angular/router';
import {
  CompetitionFilter,
  CompetitionGoalModel,
  CompetitionModel, CompetitionParticipantSchoolFilter,
  CompetitionParticipantSchoolModel, ICompetitionSchool, PublicCompetitionParticipantSchoolFilter
} from '../../../../models/competition/competition.model';
import {CultureModel} from '../../../../models/localization-and-pricing/culture.model';
import {Enums} from '../../../../models/extensions/enums';
import {TranslateService} from '@ngx-translate/core';
import {DateHelper} from '../../../../models/extensions/date-helper';
import {StringExtensionModel} from '../../../../models/extensions/string-extension.model';
import {NumberExtensionModel} from '../../../../models/extensions/number-extension.model';
import {IntegrationCompetitionAwardModel} from '../../../../models/qrcode-integrations/integration.model';
import {AdminIntegrationService} from '../../../../services/admin/admin-integration.service';
import {
  CompetitionSchoolActivityFilter,
  CompetitionSchoolActivityGroupedModel, CompetitionSchoolActivityModel
} from '../../../../models/competition/competition-activity.model';
import {group} from '@angular/animations';

@Component({
  selector: 'app-competition-detail',
  templateUrl: './competition-detail.component.html',
  styleUrls: ['./competition-detail.component.css']
})
export class CompetitionDetailComponent implements OnInit, OnDestroy {

  public comp: CompetitionModel;
  /**
   * The top participating schools - can be either participant schools or activity schools
   */
  public schools: ICompetitionSchool[] = [];
  groupedSchools: CompetitionSchoolActivityGroupedModel[] = [];

  NHelper = NumberExtensionModel;

  showAward = false;
  integrationAward: IntegrationCompetitionAwardModel = null;


  constructor(private compSv: CompetitionService,
              private adminIntSv: AdminIntegrationService,
              public tSv: TranslateService,
              private route: ActivatedRoute) { }

  ngOnInit() {
    this.route.params.subscribe((params: Params) => {
      const compId = params.competitionId;
      this.fetchCompetition(compId);
    });
  }

  ngOnDestroy() {
    if (this.comp) {
      this.comp.endCountdown();
    }
  }

  getNumberWithDelimeter(number: number, fractionalDigits = 0) {

    return NumberExtensionModel.delimiterSeparatedNumber(number, '.', fractionalDigits);

  }

  fetchCompetition(compId: string) {

    if (compId) {
      this.compSv.fetchPublicCompetitionById(compId).subscribe((comp) => {
        this.comp = comp;
        this.fetchData();
        if (this.comp.isUpcoming()) {
          // Set countdown timer
          this.setCountdown();
        }
      });

    }

  }

  fetchData() {

    // For multi school competitions
    if (this.comp.type === Enums.CompetitionTypes.multiSchool) {

      if (!this.comp.isUpcoming()) {

        const filter = new CompetitionSchoolActivityFilter();
        filter.sortByAvgAnswers = -1;
        filter.sortGroupByStartAt = 1;
        filter.limit = 3;
        this.compSv.fetchPublicSchoolActivitiesGroupedWeekly(this.comp._id, filter).subscribe((grouped) => {

          this.groupedSchools = grouped;
          if (this.groupedSchools.length > 0 && this.comp.isActive()) {
            this.schools = this.groupedSchools[this.groupedSchools.length - 1].schools as ICompetitionSchool[];
          }

        });

      }

      if (this.comp.isFinished()) {

        const filter = new PublicCompetitionParticipantSchoolFilter();
        filter.sortByAvgAnswers = -1;
        filter.limit = 3;
        this.compSv.fetchPublicParticipantSchools(this.comp._id, filter).subscribe((schools) => {
          this.schools = schools as ICompetitionSchool[];
        });

      }

    }

  }

  setCountdown() {

    this.comp.startCountdown(() => {
      this.fetchData();
    });

  }

  /**
   * checks the top schools of the week if the competition is active
   * or the top schools overall if the competition is over.
   * @param index index of the school from 0 - 3
   */
  getIsTopSchoolAvailable(index: number): boolean {

    if (this.schools.length > index) {
      if (this.schools[index].numAnswers > 0) {
        return true;
      }
    }

    return false;
  }

  /**
   * School methods
   */
  getTopSchoolName(index: number): string {

    if (index < this.schools.length) {

      if (this.schools[index].numAnswers > 0) {

        return this.schools[index].getName();
      }

    }

    return null;
  }

  getTopSchoolAvgAnswers(index: number): string {

    if (index < this.schools.length) {

      const numAnswers = this.schools[index].numAnswers;
      const numStudents = this.schools[index].numStudents;

      if (numAnswers === null || numAnswers === undefined) {
        return '0';
      }

      if (numStudents === 0 || !numStudents) {
        return numAnswers.toFixed(1);
      }

      return (numAnswers / numStudents).toFixed(1);

    }

    return '0';

  }

  getTopSchoolCountry(index: number): string {

    if (index < this.schools.length) {
      return this.schools[index].country;
    }

    return null;
  }

  getTopSchoolCountryTranslatedString(index: number) {

    const country = this.getTopSchoolCountry(index);
    return CountryModel.title(country);
  }

  getTopSchoolFlag(index: number): string {

    return CountryModel.iconURL(this.getTopSchoolCountry(index));

  }

  getTopWeeklySchools(): CompetitionSchoolActivityGroupedModel[] {

    if (!this.comp.isUpcoming()) {
      return this.groupedSchools.slice(0, this.groupedSchools.length - 1);
    }

    return this.groupedSchools;

  }

  getTopWeeklySchool(schoolIndex: number, groupIndex: number) {

    if (groupIndex < this.groupedSchools.length) {
      if (schoolIndex < this.groupedSchools[groupIndex].schools.length) {
        return this.groupedSchools[groupIndex].schools[schoolIndex];
      }
    }

    return null;

  }

  getTopWeeklySchoolName(schoolIndex: number, groupIndex: number): string {

    const school = this.getTopWeeklySchool(schoolIndex, groupIndex);
    if (school) {

      return school.schoolName;
    }

    return '';

  }

  showGoalRewardQRCode(goal: CompetitionGoalModel) {
    this.showAward = true;
    this.adminIntSv.fetchIntegrationById(goal.integrationId).subscribe((int) => {
      this.integrationAward = int as IntegrationCompetitionAwardModel;
    });
  }

  /**
   * Calculates the number of days remaining until the competition ends
   * @returns Number of days left (0 if competition ended or no end date)
   */
  daysLeft(): number {
    // Return 0 if competition or end date is not available
    if (!this.comp || !this.comp.endAt) {
      return 0;
    }

    // Get current date and end date
    const now = new Date();
    const endDate = new Date(this.comp.endAt);

    // Calculate time difference in milliseconds
    const diffTime = endDate.getTime() - now.getTime();

    // Convert milliseconds to days and round up using Math.ceil
    // Formula: milliseconds / (1000 ms * 60 sec * 60 min * 24 hours)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Return days left if positive, otherwise return 0 (competition has ended)
    return diffDays > 0 ? diffDays : 0;
  }

}
